import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AccountId, RestOptions, RestService } from '@tdwb/core';
import {
  CreateLimitRestrictions,
  CreateRestrictions,
  RetrieveRestrictions,
  StockLoanDetails,
  EnrollmentStatus,
  MonthlyPaymentHistory,
  FplDisclosure,
  FplOnLoanDetails,
  FplSecurityEarnings,
  AnnualPaymentHistory,
  StockLendingTabDetails
} from '../fpl.domain';

@Injectable()
export class FplRestService {
  constructor(private restService: RestService) {
  }

  stockLendingAccountStatus(accountId: AccountId): Observable<boolean> {
    const parameters = {
      pathVariables: { accountId }
    } as RestOptions;
    return this.restService.getResource('fullyPaidLending.accountStatus').get<boolean>(parameters);
  }

  stockLendingTabStatus(accountId: AccountId): Observable<EnrollmentStatus> {
    const parameters = {
      pathVariables: { accountId }
    } as RestOptions;
    return this.restService.getResource('fullyPaidLending.stockLendingTabStatus').get<EnrollmentStatus>(parameters);
  }

  getStockLendingTabDetails(accountId: AccountId): Observable<StockLendingTabDetails> {
    const parameters = {
      pathVariables: { accountId }
    } as RestOptions;
    return this.restService.getResource('fullyPaidLending.stockLendingTabDetails').get<StockLendingTabDetails>(parameters);
  }

  getRestrictions(accountId: AccountId): Observable<RetrieveRestrictions> {
    const parameters = {
      pathVariables: { accountId }
    } as RestOptions;
    return this.restService.getResource('fullyPaidLending.retrieveRestrictions').get<RetrieveRestrictions>(parameters);
  }

  createRestrictions(payload: CreateRestrictions): Observable<boolean> {
    return this.restService.getResource('fullyPaidLending.createRestrictions').post(payload);
  }

  deleteRestrictions(accountId: AccountId, payload: Array<number>): Observable<boolean> {
    const parameters = {
      pathVariables: { accountId }
    } as RestOptions;
    return this.restService.getResource('fullyPaidLending.deleteRestrictions').post(payload, parameters);
  }

  createLimitRestrictions(payload: CreateLimitRestrictions): Observable<boolean> {
    return this.restService.getResource('fullyPaidLending.createLimitRestrictions').post(payload);
  }

  getStockLoanDetails(cusip: string, accountId: AccountId): Observable<StockLoanDetails> {
    const parameters = {
      pathVariables: { cusip, accountId }
    } as RestOptions;
    return this.restService.getResource('fullyPaidLending.stockLoanDetails').get<StockLoanDetails>(parameters);
  }

  getMonthlyPaymentHistory(accountId: AccountId): Observable<MonthlyPaymentHistory> {
    const parameters = {
      pathVariables: { accountId }
    } as RestOptions;
    return this.restService.getResource('fullyPaidLending.monthlyPaymentHistory').get<MonthlyPaymentHistory>(parameters);
  }

  getAllSecuritiesEarningHistory(accountId: AccountId): Observable<Array<FplSecurityEarnings>> {
    const parameters = {
      pathVariables: { accountId }
    } as RestOptions;
    return this.restService.getResource('fullyPaidLending.bySecurityEarningHistory').get<Array<FplSecurityEarnings>>(parameters);
  }

  getSecurityEarningHistory(accountId: AccountId, cusip: string): Observable<Array<AnnualPaymentHistory>> {
    const parameters = {
      pathVariables: { accountId, cusip }
    } as RestOptions;
    return this.restService.getResource('fullyPaidLending.retrieveEarningHistoryBySecurity').get<Array<AnnualPaymentHistory>>(parameters);
  }

  getOnLoanDetails(accountId: AccountId): Observable<FplOnLoanDetails> {
    const parameters = {
      pathVariables: { accountId }
    } as RestOptions;
    return this.restService.getResource('fullyPaidLending.onLoanDetails').get<FplOnLoanDetails>(parameters);
  }

  getFplDisclosure(): Observable<FplDisclosure> {
    return this.restService.getResource('fullyPaidLending.retrieveFplDisclosure').get<FplDisclosure>();
  }
}
